import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useEscapeKey } from '../../lib/useEscapeKey';
import type { Media } from '../../types';
import { Button } from '../ui/Button';

interface MediaLightboxProps {
  media: Media | null;
  onClose: () => void;
}

// Convert YouTube/Vimeo URLs to embeddable format
function asEmbed(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    const host = parsed.host.toLowerCase();
    if (host.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : undefined;
    }
    if (host.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`;
    }
    if (host.includes('vimeo.com')) {
      const parts = parsed.pathname.split('/');
      const id = parts.pop();
      return id ? `https://player.vimeo.com/video/${id}` : undefined;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function MediaLightbox({ media, onClose }: MediaLightboxProps) {
  // Use custom hooks for keyboard and scroll management
  useEscapeKey(onClose, !!media);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (media) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [media]);

  const url = media?.downloadUrl || media?.externalUrl;
  const embedUrl = asEmbed(url);

  const lightboxContent = (
    <AnimatePresence>
      {media && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] safe-all bg-black/95 backdrop-blur-sm flex flex-col"
          onClick={onClose}
        >
          {/* Header with close button */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between px-5 py-4 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-sm text-slate-200 truncate max-w-[70%]">
              {media.caption || media.storagePath?.split('/').pop() || 'Media'}
            </p>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </motion.div>

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center px-4 pb-6 min-h-0"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-5xl max-h-full"
            >
              {media.type === 'video' ? (
                embedUrl ? (
                  <div className="rounded-xl overflow-hidden shadow-2xl bg-black">
                    <iframe
                      className="w-full aspect-video"
                      src={embedUrl}
                      allow="autoplay; encrypted-media; fullscreen"
                      allowFullScreen
                      title={media.caption || 'Video'}
                    />
                  </div>
                ) : (
                  <video
                    src={url}
                    controls
                    autoPlay
                    className="w-full max-h-[85vh] object-contain bg-black rounded-xl shadow-2xl"
                  />
                )
              ) : media.type === 'document' ? (
                <div className="bg-slate-900/90 rounded-xl border border-white/10 p-8 text-center text-slate-100 space-y-4 shadow-2xl">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary-500/20 flex items-center justify-center">
                    <svg className="w-8 h-8 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-lg font-semibold">Document Preview</p>
                  <p className="text-sm text-slate-400">This document will open in a new tab</p>
                  {url && (
                    <a href={url} target="_blank" rel="noreferrer">
                      <Button>Open Document</Button>
                    </a>
                  )}
                </div>
              ) : (
                <img
                  src={url || ''}
                  alt={media.caption || ''}
                  className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg shadow-2xl"
                  referrerPolicy="no-referrer"
                />
              )}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(lightboxContent, document.body);
}
